import { z } from "zod";

// Page status enum
export const PageStatusSchema = z.enum(['draft', 'published']);

// Metafields - flexible key-value storage
export const PageMetafieldsSchema = z.record(z.string(), z.unknown());

// Minimal schema reference (used in page responses)
export const PageSchemaRefSchema = z.object({
    id: z.number(),
    name: z.string(),
    label: z.string(),
});

// Editor info (used in admin responses)
export const PageEditorSchema = z.object({
    id: z.number(),
    name: z.string(),
    email: z.string(),
});

// Public page response
export const PublicPageSchema = z.object({
    id: z.number(),
    path: z.string(),
    tags: z.array(z.string()).optional().default([]),
    schema: PageSchemaRefSchema.nullable(),
    metafields: PageMetafieldsSchema,
    updated_at: z.string(),
});

// Admin page response
export const AdminPageSchema = z.object({
    id: z.number(),
    path: z.string(),
    status: PageStatusSchema,
    tags: z.array(z.string()).optional().default([]),
    schema: PageSchemaRefSchema.nullable(),
    metafields: PageMetafieldsSchema,
    last_editor: PageEditorSchema.nullable().optional(),
    versions_count: z.number().optional(),
    created_at: z.string(),
    updated_at: z.string(),
});

// Create page request body
export const CreatePageRequestSchema = z.object({
    schema_id: z.number().nullable().optional(),
    path: z.string(),
    status: PageStatusSchema.optional().default('draft'),
    tags: z.array(z.string()).optional(),
    metafields: PageMetafieldsSchema.optional(),
});

// Update page request body
export const UpdatePageRequestSchema = z.object({
    schema_id: z.number().nullable().optional(),
    path: z.string().optional(),
    status: PageStatusSchema.optional(),
    tags: z.array(z.string()).optional(),
    metafields: PageMetafieldsSchema.optional(),
});

// TypeScript type exports
export type PageStatus = z.infer<typeof PageStatusSchema>;
export type PageMetafields = z.infer<typeof PageMetafieldsSchema>;
export type PageSchemaRef = z.infer<typeof PageSchemaRefSchema>;
export type PageEditor = z.infer<typeof PageEditorSchema>;
export type PublicPage = z.infer<typeof PublicPageSchema>;
export type AdminPage = z.infer<typeof AdminPageSchema>;
export type CreatePageRequest = z.infer<typeof CreatePageRequestSchema>;
export type UpdatePageRequest = z.infer<typeof UpdatePageRequestSchema>;
